<template>
  <div>
    <div
      :class="fileInputTargetClass"
      style="font-size: 15px !important;"
    ></div>

    <div :class="progressBarTargetClass"></div>

    <div
      v-if="hasFiles"
      class="upload-metrics mb-3 mt-2"
    >
      <div
        v-if="rateLimitRemainingSeconds != null"
        class="rate-limit-countdown mb-2 p-2 rounded"
      >
        <span>
          <span aria-hidden="true">⚠️</span>
          Rate limited. Retrying upload in <strong>{{ rateLimitRemainingSeconds }}s</strong>…
        </span>
      </div>
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <span class="text-muted">Progress:</span>
          <strong class="ml-2">{{ overallProgress }}%</strong>
        </div>
        <template v-if="overallProgress > 0 && overallProgress < 100">
          <div>
            <span class="text-muted">Speed:</span>
            <strong class="ml-2">{{ uploadMetrics.speed }}</strong>
          </div>
          <div>
            <span class="text-muted">ETA:</span>
            <strong class="ml-2">{{ uploadMetrics.eta }}</strong>
          </div>
        </template>
        <div v-else-if="overallProgress === 0 && hasFiles && rateLimitRemainingSeconds == null" class="text-muted small">
          Queued / starting…
        </div>
        <div v-else-if="overallProgress === 100" class="badge badge-pill badge-success">
          Completed
        </div>
      </div>
    </div>

    <div v-if="failedFiles.length > 0" class="failed-files-actions mb-3">
      <button
        type="button"
        @click="retryAllFailed"
        class="btn btn-warning btn-sm"
        :disabled="isRetryingAll"
      >
        <i class="flaticon2-refresh"></i> Retry All Failed ({{ failedFiles.length }})
      </button>
    </div>

    <!-- Uploaded files list -->
    <div v-show="showUploadedFiles" class="uploaded-files mt-4">
      <h5>Uploaded files:</h5>
      <ol></ol>
    </div>
  </div>
</template>

<script>
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import FileInput from "@uppy/file-input";
import ProgressBar from "@uppy/progress-bar";
import { v4 as uuidv4 } from "uuid";
import { mapGetters } from "vuex";
import { UPLOAD_CONFIG } from "@/config/uploadConfig";
import { globalNetworkMonitor } from "@/helpers/networkMonitor";
import { parseRetryAfter } from "@/helpers/uppyRetryHandler";
import { UploadProgressTracker } from "@/helpers/uploadProgressTracker";
import {
  generatePresignedUrl,
  confirmUpload
} from "@/services/cdnUploadService";
import {
  initiateMultipartUpload,
  getPresignedPartUrl,
  completeMultipartUpload as completeMultipartUploadService,
  abortMultipartUpload
} from "@/services/multipartUploadService";

import "@uppy/core/dist/style.css";
import "@uppy/file-input/dist/style.css";
import "@uppy/progress-bar/dist/style.css";

export default {
  name: "UppyXHRFileUploader",
  props: {
    title: {
      type: String,
      default: "Choose Files",
    },
    url: {
      type: String,
      required: true,
    },
    maxFiles: {
      type: Number,
      default: null,
    },
    fileTypes: {
      type: Array,
      default: null,
    },
    meta: {
      type: Object,
      default: () => {
        return {};
      },
    },
    headers: {
      type: Object,
      default: () => {
        return {};
      },
    },
    bgColor: {
      type: String,
      default: "#FFFCF1",
    },
    dashboardHeight: {
      type: String,
      default: "100% !important",
    },
    imageType: {
      type: String,
      default: "sample",
    },
  },
  data() {
    return {
      uppyId: uuidv4(),
      uppy: null,
      showUploadedFiles: false,
      failedFiles: [],
      isDestroyingComponent: false,
      networkMonitor: null,
      progressTracker: null,
      overallProgress: 0,
      uploadMetrics: {
        speed: "0 B/s",
        eta: "calculating...",
      },
      isRetryingAll: false,
      rateLimitRetryTimeouts: {},
      rateLimitRemainingSeconds: null,
      rateLimitCountdownIntervalId: null,
    };
  },
  computed: {
    ...mapGetters({
      sampleImages: "order/sampleImages",
    }),
    fileInputTargetClass() {
      return `Uppy-${this.uppyId}`;
    },
    progressBarTargetClass() {
      return `UppyProgressBar-${this.uppyId}`;
    },
    uploadedFilesTargetClass() {
      return `uploaded-files-${this.uppyId}`;
    },

    acceptFiles() {
      if (!this.fileTypes || this.fileTypes.length === 0) {
        return null;
      }
      return this.fileTypes.toString();
    },
    hasFiles() {
      if (!this.uppy) {
        return false;
      }
      return !!this.uppy.getFiles().length;
    },
    canUploadFiles() {
      if (!this.maxFiles || !this.hasFiles) {
        return true;
      }
      return this.uppy.getFiles().length < this.maxFiles;
    },
  },

  mounted() {
    const orderToken = this.meta.token;
    const imageType = this.imageType;

    this.uppy = new Uppy({
      id: this.uppyId,
      autoProceed: true,
      allowMultipleUploadBatches: true,
      restrictions: {
        maxNumberOfFiles: this.maxFiles,
        allowedFileTypes: this.fileTypes,
      },
      meta: this.meta,
      onBeforeFileAdded: (currentFile, files) => this.onBeforeFileAdded(currentFile, files),
    })
      .use(FileInput, {
        target: "." + this.fileInputTargetClass,
        locale: { strings: { chooseFiles: this.title } },
      })
      .use(ProgressBar, {
        target: "." + this.progressBarTargetClass,
        hideAfterFinish: false,
      })
      .use(AwsS3, this.getAwsS3Options(orderToken, imageType));

    this.progressTracker = new UploadProgressTracker();
    if (UPLOAD_CONFIG.ENABLE_AUTO_RETRY_ON_RECONNECT) {
      this.networkMonitor = globalNetworkMonitor;
      this.networkMonitor.onReconnect(() => this.retryFailedUploadsAfterReconnect());
    }

    this.uppy
      .on("restriction-failed", (file, error) => this.onRestrictionFailed(file, error))
      .on("upload-success", (file, response) => this.onUploadSuccess(file, response))
      .on("upload-error", (file, error) => this.onUploadError(file, error))
      .on("file-removed", (file) => this.onFileRemoved(file))
      .on("upload", () => {
        this.progressTracker.start();
        this.$emit("upload", true);
      })
      .on("progress", (progress) => this.updateProgressFromUppy(progress))
      .on("complete", (result) => this.onComplete(result));
  },

  beforeDestroy() {
    this.isDestroyingComponent = true;
    this.clearRateLimitState();

    if (this.networkMonitor) {
      this.networkMonitor.removeReconnectListener(this.retryFailedUploadsAfterReconnect);
    }

    if (this.progressTracker) {
      this.progressTracker.reset();
    }

    if (this.uppy) {
      this.uppy.close();
    }
  },

  methods: {
    retryFile(fileID) {
      this.uppy.retryUpload(fileID);
      this.failedFiles = this.failedFiles.filter((failedFile) => failedFile?.id !== fileID);
    },

    retryAllFailed() {
      if (this.failedFiles.length === 0) {
        return;
      }

      const count = this.failedFiles.length;

      this.uppy.info(`Retrying ${count} failed upload(s)...`, "info", 3000);

      this.uppy.retryAll();
    },

    retryFailedUploadsAfterReconnect() {
      if (this.failedFiles.length === 0 || !this.uppy) {
        return;
      }

      const count = this.failedFiles.length;

      this.$swal({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Network Restored",
        text: `Auto-retrying ${count} failed upload(s)...`,
        showConfirmButton: false,
        timer: 3000,
      });
      
      this.uppy.info(
        `Network reconnected. Auto-retrying ${count} failed upload(s)...`,
        "info",
        3000
      );

      setTimeout(() => this.uppy.retryAll(), 1000);
    },

    isRateLimitError(error) {
      return (
        error?.source?.status === 429 ||
        error?.message?.includes("429") ||
        error?.message?.toLowerCase().includes("too many requests")
      );
    },

    clearRateLimitCountdown() {
      if (this.rateLimitCountdownIntervalId) {
        clearInterval(this.rateLimitCountdownIntervalId);
        this.rateLimitCountdownIntervalId = null;
      }

      this.rateLimitRemainingSeconds = null;
    },

    clearRateLimitState() {
      Object.values(this.rateLimitRetryTimeouts).forEach((id) => clearTimeout(id));
      this.rateLimitRetryTimeouts = {};

      this.clearRateLimitCountdown();
    },

    scheduleRateLimitRetry(file, error) {
      if (this.rateLimitRetryTimeouts[file.id]) {
        clearTimeout(this.rateLimitRetryTimeouts[file.id]);
        delete this.rateLimitRetryTimeouts[file.id];
      }

      if (this.rateLimitCountdownIntervalId) {
        clearInterval(this.rateLimitCountdownIntervalId);
        this.rateLimitCountdownIntervalId = null;
      }

      const retryAfter =
        error?.source?.retryAfter ??
        error?.source?.headers?.["retry-after"] ??
        error?.source?.headers?.["Retry-After"];
      const delayMs = parseRetryAfter(retryAfter);
      const delaySec = Math.ceil(delayMs / 1000);

      this.rateLimitRemainingSeconds = delaySec;

      this.rateLimitCountdownIntervalId = setInterval(() => {
        this.rateLimitRemainingSeconds = Math.max(0, this.rateLimitRemainingSeconds - 1);
        if (this.rateLimitRemainingSeconds <= 0 && this.rateLimitCountdownIntervalId) {
          clearInterval(this.rateLimitCountdownIntervalId);
          this.rateLimitCountdownIntervalId = null;
        }
      }, 1000);

      this.uppy.info(`Upload rate limited. Automatically retrying in ${delaySec}s…`, "info", 3000);

      this.$swal({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Rate limited",
        text: `Retrying in ${delaySec}s...`,
        showConfirmButton: false,
        timer: 3000,
      });

      const timeoutId = setTimeout(() => {
        delete this.rateLimitRetryTimeouts[file.id];
        this.clearRateLimitCountdown();

        if (this.isDestroyingComponent || !this.uppy) return;
        if (this.uppy.getFile(file.id)) {
          this.uppy.retryUpload(file.id);
        }
      }, delayMs);

      this.rateLimitRetryTimeouts[file.id] = timeoutId;

      if (!this.failedFiles.find((failedFile) => failedFile?.id === file.id)) {
          this.failedFiles.push({
            id: file.id,
            name: file.name,
            error: error.message,
          });
        }
    },

    getChunkSizeForFile(file) {
      const fileSize = file.size;
      let chunkSize = UPLOAD_CONFIG.CHUNK_SIZE;

      if (fileSize > 100 * 1024 * 1024) {
        chunkSize = Math.min(UPLOAD_CONFIG.MAX_CHUNK_SIZE, fileSize / 20);
      }

      chunkSize = Math.max(UPLOAD_CONFIG.MIN_CHUNK_SIZE, Math.min(UPLOAD_CONFIG.MAX_CHUNK_SIZE, chunkSize));
      return Math.floor(chunkSize);
    },

    updateProgressFromUppy(progress) {
      this.overallProgress = Math.round(progress);

      const files = this.uppy.getFiles();
      let totalBytes = 0;
      let uploadedBytes = 0;

      files.forEach((file) => {
        if (file.progress) {
          totalBytes += file.progress.bytesTotal || file.size;
          uploadedBytes += file.progress.bytesUploaded || 0;
        }
      });

      this.progressTracker.update(uploadedBytes, totalBytes);
      this.uploadMetrics.speed = this.progressTracker.getFormattedSpeed();
      this.uploadMetrics.eta = this.progressTracker.getFormattedETA(uploadedBytes, totalBytes);
    },

    onBeforeFileAdded(currentFile, files) {
      const existingInQueue = Object.values(files).find(
        (existingFile) => existingFile?.name === currentFile.name && existingFile?.size === currentFile.size
      );

      if (existingInQueue) {
        this.$swal({
          icon: "warning",
          title: "Duplicate File",
          text: `${currentFile.name} is already in the upload queue.`,
        });

        return false;
      }

      const alreadyUploaded = this.sampleImages.find(
        (img) => img.original_name === currentFile.name && img.file_size === currentFile.size
      );

      if (alreadyUploaded) {
        this.$swal({
          icon: "warning",
          title: "Already Uploaded",
          text: `${currentFile.name} has already been uploaded.`,
        });

        return false;
      }

      return true;
    },

    getAwsS3Options(orderToken, imageType) {
      return {
        id: "AwsS3Sample",
        limit: UPLOAD_CONFIG.MAX_PARALLEL_FILES,
        retryDelays: UPLOAD_CONFIG.RETRY_DELAYS.slice(0, UPLOAD_CONFIG.MAX_RETRIES),
        shouldUseMultipart: (file) => file.size >= UPLOAD_CONFIG.MULTIPART_THRESHOLD,
        getChunkSize: (file) => this.getChunkSizeForFile(file),
        async getUploadParameters(file) {
          const data = await generatePresignedUrl(file.data, orderToken, imageType);
          file.meta.cdnUrl = data.cdn_url;
          file.meta.objectKey = data.object_key;

          return {
            method: "PUT",
            url: data.presigned_url,
            headers: { "Content-Type": file.type || "application/octet-stream" },
          };
        },
        async createMultipartUpload(file) {
          const data = await initiateMultipartUpload(file.data, orderToken, imageType);
          file.meta.cdnUrl = data.cdn_url;
          file.meta.objectKey = data.object_key;

          return { uploadId: data.upload_id, key: data.object_key };
        },
        async signPart(file, { uploadId, key, partNumber }) {
          const data = await getPresignedPartUrl(uploadId, key, partNumber, orderToken);
          if (data.already_uploaded && data.etag) {
            return { url: data.presigned_url, headers: {}, expires: 0 };
          }

          return { url: data.presigned_url };
        },
        async completeMultipartUpload(file, { uploadId, key, parts }) {
          const formattedParts = parts.map((p) => ({
            part_number: p.PartNumber,
            etag: p.ETag || p.etag,
          }));

          const data = await completeMultipartUploadService(
            uploadId,
            key,
            formattedParts,
            file.name,
            file.size,
            file.meta.cdnUrl,
            orderToken,
            imageType
          );

          return { location: data.cdn_url };
        },
        async abortMultipartUpload(file, { uploadId, key }) {
          await abortMultipartUpload(uploadId, key, orderToken);
        },
      };
    },

    onUploadSuccess(file, response) {
      const orderToken = this.meta.token;
      const imageType = this.imageType;

      if (file.size < UPLOAD_CONFIG.MULTIPART_THRESHOLD) {
        confirmUpload({
          token: orderToken,
          object_key: response.body?.object_key || file.meta.objectKey,
          filename: file.name,
          file_size: file.size,
          cdn_url: response.uploadURL || response.body?.cdn_url,
          image_type: imageType,
        })
          .then((imageRecord) => this.$emit("uploaded", file, { body: imageRecord }))
          .catch((err) => {
            console.error("Error confirming upload:", err);
            this.$emit("upload-error", file, err);
          });
      } else {
        this.$emit("uploaded", file, response);
      }

      this.showUploadedFiles = true;
      const li = document.createElement("li");
      li.appendChild(document.createTextNode(file.name));

      const ol = document.querySelector(".uploaded-files ol");
      if (ol) ol.appendChild(li);

      this.failedFiles = this.failedFiles.filter((f) => f.id !== file.id);
    },

    onUploadError(file, error) {
      if (this.isRateLimitError(error)) {
        this.scheduleRateLimitRetry(file, error);

        return;
      }

      if (!this.failedFiles.find((f) => f.id === file.id)) {
        this.failedFiles.push({ id: file.id, name: file.name, error: error.message });
      }

      this.$swal({
        icon: "error",
        title: "Upload Failed",
        html: `<p><strong>${file.name}</strong> failed to upload.</p><p class="text-muted">${error.message}</p>`,
        showCancelButton: true,
        confirmButtonText: "Retry",
        cancelButtonText: "Remove",
      }).then((result) => {
        if (result.isConfirmed) {
          this.uppy.retryUpload(file.id);
        } else {
          this.uppy.removeFile(file.id);
          this.failedFiles = this.failedFiles.filter((f) => f.id !== file.id);
        }
      });
    },

    onRestrictionFailed(file, error) {
      const msg = error?.message || "File was not added.";

      this.$swal({
        icon: "warning",
        title: "File was not added. Please try again.",
        text: file?.name ? `${file.name}: ${msg}` : msg,
      });
    },

    onComplete(result) {
      this.progressTracker.reset();
      const { successful, failed } = result;

      if (failed.length === 0 && successful.length > 0) {
        if (this.overallProgress === 100) {
          this.clearRateLimitState();
          
          this.$swal({
            icon: "success",
            title: "Upload Complete!",
            text: `Successfully uploaded ${successful.length} sample file(s).`,
          });
        } else {
          this.uppy.info(
            `Successfully uploaded ${successful.length} file(s). More remaining...`,
            "info",
            3000
          );
        }
      } else if (failed.length > 0) {
        this.$swal({
          icon: "warning",
          title: "Some Uploads Failed",
          html: `<p><strong>${successful.length}</strong> file(s) uploaded.</p><p><strong>${failed.length}</strong> file(s) upload failed.</p><p>You can retry failed uploads.</p>`,
        });
      }

      this.$emit("complete", result);
    },

    onFileRemoved(file) {
      if (!this.isDestroyingComponent) {
        this.$emit("removed", file);
      }

      this.failedFiles = this.failedFiles.filter((failedFile) => failedFile?.id !== file.id);
    },
  },
};
</script>

<style lang="css">
.uppy-FileInput-btn {
  font-size: 15px !important;
}
.uploaded-files ol li {
  padding: 0.25rem !important;
  font-size: 15px;
}

.upload-metrics {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
  font-size: 0.9rem;
}

.rate-limit-countdown {
  background-color: #faf6ec;
  border: 1px solid #e38800;
  font-size: 0.9rem;
}

.rate-limit-countdown span {
  color: #e38800;
  font-weight: 500;
}

.failed-files-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
